import { Texture } from "three/webgpu";
import type { GpuContext } from "./GpuContext.js";
import { configureTexture, type DrawableTexture } from "./texture.js";

export interface VideoTextureOptions {
  /** Needed for a video from another origin; without it the upload is refused. */
  crossOrigin?: string | null;
  loop?: boolean;
  muted?: boolean;
  autoplay?: boolean;
  /** Sample smoothly when scaled. Off for pixel-art or exact-pixel playback. */
  smooth?: boolean;
}

/** `HTMLMediaElement.HAVE_CURRENT_DATA` — there is a frame to copy. */
const HAVE_CURRENT_DATA = 2;

/**
 * `requestVideoFrameCallback` where the browser has it.
 *
 * Written as an `Omit` rather than an extension because the DOM library
 * declares these as required once it knows about them, and a plain interface
 * redeclaring them optional is an error. This shape typechecks either way.
 */
type FrameCallbackVideo = Omit<
  HTMLVideoElement,
  "requestVideoFrameCallback" | "cancelVideoFrameCallback"
> & {
  requestVideoFrameCallback?: (callback: () => void) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

/**
 * A video decoded by the browser and copied into a texture, frame by frame.
 *
 * The copy is what makes it an ordinary `DrawableTexture`: the video element is
 * the texture's image, and marking it dirty has three copy the current frame on
 * its next use, so a video draws through `rc.drawTexture` with nothing special
 * in the pipeline.
 *
 * Nothing is uploaded until there is a new frame to upload. Where the browser
 * offers `requestVideoFrameCallback` that is exact — a 24fps film on a 144Hz
 * display uploads 24 times a second, not 144 — and elsewhere it falls back to
 * copying whenever asked.
 */
export class VideoTexture implements DrawableTexture {
  readonly video: HTMLVideoElement;

  #gpu: GpuContext;
  #texture: Texture;
  /** Whether a cross-origin frame has been checked for readability yet. */
  #checked = false;
  #width = 1;
  #height = 1;
  #smooth: boolean;
  #destroyed = false;
  #dirty = true;
  /** Playhead at the last upload, as a second opinion on "is this new?". */
  #lastTime = -1;
  #handle = 0;
  #usesFrameCallback = false;
  #error: unknown = null;
  #ownsVideo: boolean;

  /** Called when a new frame has arrived, so a control can ask for a repaint. */
  onFrame: (() => void) | null = null;

  constructor(gpu: GpuContext, video: HTMLVideoElement, options: VideoTextureOptions = {}) {
    this.#gpu = gpu;
    this.video = video;
    this.#smooth = options.smooth ?? true;
    this.#ownsVideo = false;
    if (options.crossOrigin !== undefined) video.crossOrigin = options.crossOrigin;
    if (options.loop !== undefined) video.loop = options.loop;
    if (options.muted !== undefined) video.muted = options.muted;
    if (options.autoplay !== undefined) video.autoplay = options.autoplay;
    // Real from the first paint, so the drawing path needs no null check; three
    // skips the upload until the element has a frame.
    this.#texture = this.#allocate();
    this.#watch();
  }

  /**
   * A detached `<video>` playing `url`.
   *
   * Detached on purpose: Webwin draws its entire UI on one canvas, and putting
   * a real video element in the page would float it above every window in the
   * desktop, ignoring z-order, clipping and the window it belongs to. The
   * element is only ever a decoder.
   */
  static fromUrl(gpu: GpuContext, url: string, options: VideoTextureOptions = {}): VideoTexture {
    const video = document.createElement("video");
    // Both required to decode without a user gesture on mobile, and harmless
    // elsewhere: `muted` satisfies the autoplay policy, `playsInline` stops
    // iOS taking the video fullscreen the moment it plays.
    video.playsInline = true;
    video.preload = "auto";
    if (options.crossOrigin !== undefined) video.crossOrigin = options.crossOrigin;
    video.src = url;
    const texture = new VideoTexture(gpu, video, options);
    texture.#ownsVideo = true;
    return texture;
  }

  get width(): number {
    return this.#width;
  }

  get height(): number {
    return this.#height;
  }

  get texture(): Texture {
    return this.#texture;
  }

  /** True once a frame has been decoded and the real size is known. */
  get ready(): boolean {
    return !this.#destroyed && this.video.readyState >= HAVE_CURRENT_DATA && this.video.videoWidth > 0;
  }

  /**
   * True when the browser tells us about each new frame.
   *
   * A control uses this to decide whether it needs a frame clock of its own:
   * with the callback, painting follows the video; without it, something has
   * to keep the desktop rendering.
   */
  get usesFrameCallback(): boolean {
    return this.#usesFrameCallback;
  }

  /** Set when an upload failed — a cross-origin video without CORS, usually. */
  get error(): unknown {
    return this.#error;
  }

  get destroyed(): boolean {
    return this.#destroyed;
  }

  /** Force the next `update` to copy, whether or not a new frame arrived. */
  invalidate(): void {
    this.#dirty = true;
  }

  /**
   * Copy the current frame, if there is a new one. Returns whether it did.
   *
   * Safe to call while recording a frame: a queue copy is not a render pass,
   * and Webwin records the whole control tree before opening one.
   */
  update(): boolean {
    if (this.#destroyed || this.#error !== null) return false;
    const video = this.video;
    if (video.readyState < HAVE_CURRENT_DATA) return false;
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (width <= 0 || height <= 0) return false;

    if (width !== this.#width || height !== this.#height) {
      // An adaptive stream can change resolution mid-playback, and a texture
      // keeps the size it was first uploaded at.
      const previous = this.#texture;
      this.#texture = this.#allocate();
      previous.dispose();
      this.#width = width;
      this.#height = height;
      this.#dirty = true;
    }
    // The frame callback is the precise signal, but it is not guaranteed to
    // arrive — a backgrounded tab is the usual reason. The playhead moving is
    // the coarser one, and between them a moving picture never stops updating
    // while a still one still costs nothing.
    if (!this.#dirty && this.#usesFrameCallback && video.currentTime === this.#lastTime) {
      return false;
    }
    this.#dirty = false;
    this.#lastTime = video.currentTime;

    if (!this.#checked) {
      this.#checked = true;
      // A cross-origin frame without CORS headers cannot be uploaded, and the
      // upload happens inside three's render where the failure would take the
      // whole frame with it. Reading one pixel here asks the same question.
      try {
        const probe = new OffscreenCanvas(1, 1).getContext("2d");
        probe?.drawImage(video, 0, 0, 1, 1);
        probe?.getImageData(0, 0, 1, 1);
      } catch (err) {
        this.#error = err;
        return false;
      }
    }
    this.#texture.needsUpdate = true;
    return true;
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    const video = this.video as FrameCallbackVideo;
    if (this.#handle !== 0) video.cancelVideoFrameCallback?.(this.#handle);
    this.#handle = 0;
    this.onFrame = null;
    this.#texture.dispose();
    if (this.#ownsVideo) {
      video.pause();
      // Clearing `src` and reloading is what actually releases the decoder and
      // stops the network fetch; dropping the reference alone does not.
      video.removeAttribute("src");
      video.load();
    }
  }

  #allocate(): Texture {
    const texture = configureTexture(new Texture(this.video), this.#smooth, "clamp", false, this.#gpu.maxAnisotropy);
    texture.name = "webwin.video";
    return texture;
  }

  #watch(): void {
    const video = this.video as FrameCallbackVideo;
    if (typeof video.requestVideoFrameCallback !== "function") return;
    this.#usesFrameCallback = true;
    const step = (): void => {
      if (this.#destroyed) return;
      this.#dirty = true;
      this.onFrame?.();
      this.#handle = video.requestVideoFrameCallback!(step);
    };
    this.#handle = video.requestVideoFrameCallback(step);
  }
}
